import * as React from "react";
import { Button, IconButton } from "../components/Button";
import { Field, Input, Select, Checkbox } from "../components/Form";
import { Badge } from "../components/Badge";
import { Banner } from "../components/States";
import { Icon } from "../components/Icon";
import { useAsync } from "../general/useAsync";
import * as Management from "../management/ManagementStore";
import * as Procedures from "../procedures/ProceduresStore";
import * as Activities from "../activities/ActivitiesStore";
import { CalculationScope, SCOPE_KINDS, SCOPE_LABELS, ScopeKind } from "./scopes";

/**
 * Builds the scope list for a calculation run or preview.
 *
 * The rule that shapes this UI: multiple scopes **intersect**. Two scopes that do not overlap is a
 * 400 from the server, not an empty result - so the builder says so out loud rather than letting
 * someone discover it as an error. One scope is the common case.
 *
 * Each kind needs different ids, so the row renders only the picker its kind actually uses.
 */

export interface ScopeBuilderProps {
    scopes: CalculationScope[];
    onChange: (scopes: CalculationScope[]) => void;
}

const HINTS: Record<ScopeKind, string> = {
    Assignment: "One specific procedure assignment. The narrowest scope, and what the procedure screen uses.",
    QualificationModule: "Everything calculated for one subject, cohort and cycle.",
    AssessmentGroup: "One branch of a module's assessment tree.",
    TeachingGroup: "Narrows to the students in one class group.",
    Student: "Narrows to particular students, by identity-server user id.",
    QualificationProgramLevelCycle: "A whole cohort in one cycle. Needs both a program level and a cycle.",
};

export const ScopeBuilder: React.FC<ScopeBuilderProps> = ({ scopes, onChange }) => {
    const update = (index: number, patch: Partial<CalculationScope>) =>
        onChange(scopes.map((s, i) => (i === index ? { ...s, ...patch } : s)));

    const add = () => onChange([...scopes, { kind: "QualificationModule", ids: [] }]);

    return (
        <div className="space-y-3">
            {scopes.length === 0 && (
                <Banner tone="info">
                    No scope selected. Add at least one - a run with no scope has nothing to calculate.
                </Banner>
            )}

            {scopes.map((scope, index) => (
                <ScopeRow
                    key={index}
                    scope={scope}
                    index={index}
                    onChange={(patch) => update(index, patch)}
                    onRemove={() => onChange(scopes.filter((_, i) => i !== index))}
                />
            ))}

            <div className="flex items-center gap-3">
                <Button size="sm" variant="outline-primary" icon={["fad", "plus"]} onClick={add}>
                    Add scope
                </Button>
                {scopes.length > 1 && (
                    <span className="text-xs text-text-weak">
                        Scopes <strong>intersect</strong>. Only what matches every one of them is calculated.
                    </span>
                )}
            </div>
        </div>
    );
};

const ScopeRow: React.FC<{
    scope: CalculationScope;
    index: number;
    onChange: (patch: Partial<CalculationScope>) => void;
    onRemove: () => void;
}> = ({ scope, index, onChange, onRemove }) => {
    const kind = scope.kind as ScopeKind;

    return (
        <div className="rounded-lg border border-border bg-surface p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Badge tone="neutral">Scope {index + 1}</Badge>
                    <span className="text-xs text-text-weak">{HINTS[kind] ?? ""}</span>
                </div>
                <IconButton icon={["fad", "xmark"]} label={`Remove scope ${index + 1}`} onClick={onRemove} />
            </div>

            <Field label="Kind" className="mb-3">
                <Select
                    value={scope.kind}
                    onChange={(e) =>
                        // Ids belong to the old kind, so they are cleared rather than sent as
                        // identifiers of the wrong type.
                        onChange({
                            kind: e.target.value,
                            ids: [],
                            includeDescendants: undefined,
                            qualificationProgramLevelID: undefined,
                            assessmentCycleID: undefined,
                        })
                    }
                >
                    {SCOPE_KINDS.map((k) => (
                        <option key={k} value={k}>
                            {SCOPE_LABELS[k]}
                        </option>
                    ))}
                </Select>
            </Field>

            {kind === "Assignment" && <AssignmentPicker ids={scope.ids} onChange={(ids) => onChange({ ids })} />}
            {kind === "QualificationModule" && <ModuleIdPicker ids={scope.ids} onChange={(ids) => onChange({ ids })} />}
            {kind === "TeachingGroup" && <TeachingGroupPicker ids={scope.ids} onChange={(ids) => onChange({ ids })} />}
            {kind === "Student" && <StudentPicker ids={scope.ids} onChange={(ids) => onChange({ ids })} />}

            {kind === "AssessmentGroup" && (
                <>
                    <AssessmentGroupPicker ids={scope.ids} onChange={(ids) => onChange({ ids })} />
                    <Checkbox
                        className="mt-3"
                        label="Include everything below these groups"
                        hint="Only meaningful for this kind of scope."
                        checked={scope.includeDescendants ?? false}
                        onChange={(e) => onChange({ includeDescendants: e.target.checked })}
                    />
                </>
            )}

            {kind === "QualificationProgramLevelCycle" && (
                <CohortCyclePicker
                    programLevelID={scope.qualificationProgramLevelID}
                    cycleID={scope.assessmentCycleID}
                    onChange={(patch) => onChange(patch)}
                />
            )}
        </div>
    );
};

/** Select-plus-Add producing a removable chip list. Used by every id-based kind. */
const ChipPicker: React.FC<{
    label: string;
    ids: string[];
    options: Array<{ value: string; label: string }>;
    loading?: boolean;
    error?: string;
    onChange: (ids: string[]) => void;
}> = ({ label, ids, options, loading, error, onChange }) => {
    const [pending, setPending] = React.useState("");
    const available = options.filter((o) => !ids.includes(o.value));
    const labelOf = (id: string) => options.find((o) => o.value === id)?.label ?? id;

    return (
        <div>
            <label className="mb-1 block text-sm font-medium text-text">{label}</label>

            <div className="flex gap-2">
                <Select
                    value={pending}
                    className="flex-1"
                    placeholder={loading ? "Loading..." : available.length ? "Select" : "Nothing left to add"}
                    disabled={loading || !available.length}
                    onChange={(e) => setPending(e.target.value)}
                >
                    {available.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </Select>
                <Button
                    variant="outline-secondary"
                    icon={["fad", "plus"]}
                    disabled={!pending}
                    onClick={() => {
                        onChange([...ids, pending]);
                        setPending("");
                    }}
                >
                    Add
                </Button>
            </div>

            {error && <p className="mt-1 text-sm text-danger-text">{error}</p>}

            <div className="mt-2 flex flex-wrap gap-2">
                {ids.length === 0 && <span className="text-xs text-text-weak">Nothing selected yet.</span>}
                {ids.map((id) => (
                    <span
                        key={id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-2.5 py-1 text-xs text-primary-text"
                    >
                        {labelOf(id)}
                        <button type="button" onClick={() => onChange(ids.filter((i) => i !== id))} aria-label={`Remove ${labelOf(id)}`}>
                            <Icon icon={["fad", "xmark"]} />
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );
};

const AssignmentPicker: React.FC<{ ids: string[]; onChange: (ids: string[]) => void }> = ({ ids, onChange }) => {
    const assignments = useAsync(() => Procedures.listAssignments(), [], { initial: [] });

    return (
        <ChipPicker
            label="Assignments"
            ids={ids}
            loading={assignments.isLoading}
            error={assignments.error}
            onChange={onChange}
            options={(assignments.data ?? []).map((a) => ({
                value: a.id ?? "",
                label: `${a.procedureName} v${a.versionNumber} - ${a.qualificationModuleName}${a.isActive ? "" : " (inactive)"}`,
            }))}
        />
    );
};

const ModuleIdPicker: React.FC<{ ids: string[]; onChange: (ids: string[]) => void }> = ({ ids, onChange }) => {
    const modules = useAsync(() => Management.listQualificationModules(), [], { initial: [] });

    return (
        <ChipPicker
            label="Qualification modules"
            ids={ids}
            loading={modules.isLoading}
            error={modules.error}
            onChange={onChange}
            options={(modules.data ?? []).map((m) => ({ value: m.id ?? "", label: Management.qualificationModuleLabel(m) }))}
        />
    );
};

const TeachingGroupPicker: React.FC<{ ids: string[]; onChange: (ids: string[]) => void }> = ({ ids, onChange }) => {
    const groups = useAsync(() => Management.listTeachingGroups(), [], { initial: [] });

    return (
        <ChipPicker
            label="Teaching groups"
            ids={ids}
            loading={groups.isLoading}
            error={groups.error}
            onChange={onChange}
            options={(groups.data ?? []).map((g) => ({
                value: g.id ?? "",
                label: `${g.name}${g.module ? ` - ${Management.qualificationModuleLabel(g.module)}` : ""}`,
            }))}
        />
    );
};

/**
 * Assessment groups live inside one module's tree, so a module has to be chosen first just to have
 * a list to pick from. That module is not itself part of the scope.
 */
const AssessmentGroupPicker: React.FC<{ ids: string[]; onChange: (ids: string[]) => void }> = ({ ids, onChange }) => {
    const [moduleID, setModuleID] = React.useState("");

    const modules = useAsync(() => Management.listQualificationModules(), [], { initial: [] });
    const structure = useAsync(() => (moduleID ? Activities.getModuleStructure(moduleID) : Promise.resolve(undefined)), [moduleID], {
        enabled: !!moduleID,
    });

    return (
        <div className="space-y-3">
            <Field label="Find groups in" className="mb-0" hint="Used only to list the groups. The module itself is not part of the scope.">
                <Select value={moduleID} placeholder="Select a qualification module" onChange={(e) => setModuleID(e.target.value)}>
                    {(modules.data ?? []).map((m) => (
                        <option key={m.id} value={m.id}>
                            {Management.qualificationModuleLabel(m)}
                        </option>
                    ))}
                </Select>
            </Field>

            <ChipPicker
                label="Assessment groups"
                ids={ids}
                loading={structure.isLoading}
                error={structure.error}
                onChange={onChange}
                options={(structure.data?.groups ?? []).map((g) => ({
                    value: g.id ?? "",
                    label: `${"  ".repeat(g.depth ?? 0)}${g.name}${g.assessmentGroupTypeName ? ` (${g.assessmentGroupTypeName})` : ""}`,
                }))}
            />
        </div>
    );
};

/** Students are identity-server user ids, so there is nothing local to list - they are typed in. */
const StudentPicker: React.FC<{ ids: string[]; onChange: (ids: string[]) => void }> = ({ ids, onChange }) => {
    const [pending, setPending] = React.useState("");

    const add = () => {
        const value = pending.trim();
        if (!value || ids.includes(value)) return;
        onChange([...ids, value]);
        setPending("");
    };

    return (
        <div>
            <label className="mb-1 block text-sm font-medium text-text">Student ids</label>
            <div className="flex gap-2">
                <Input
                    value={pending}
                    className="flex-1"
                    placeholder="Identity-server user id"
                    onChange={(e) => setPending(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            add();
                        }
                    }}
                />
                <Button variant="outline-secondary" icon={["fad", "plus"]} disabled={!pending.trim()} onClick={add}>
                    Add
                </Button>
            </div>
            <p className="mt-1 text-xs text-text-weak">Students are not stored in this database, so there is no list to pick from.</p>

            <div className="mt-2 flex flex-wrap gap-2">
                {ids.map((id) => (
                    <span
                        key={id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-2.5 py-1 font-mono text-xs text-primary-text"
                    >
                        {id}
                        <button type="button" onClick={() => onChange(ids.filter((i) => i !== id))} aria-label={`Remove ${id}`}>
                            <Icon icon={["fad", "xmark"]} />
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );
};

/** This kind carries its ids in two dedicated fields rather than the `ids` array, and needs both. */
const CohortCyclePicker: React.FC<{
    programLevelID?: string;
    cycleID?: string;
    onChange: (patch: Partial<CalculationScope>) => void;
}> = ({ programLevelID, cycleID, onChange }) => {
    const levels = useAsync(() => Management.listQualificationProgramLevels(), [], { initial: [] });
    const cycles = useAsync(() => Management.listAssessmentCycles(), [], { initial: [] });

    const incomplete = !programLevelID || !cycleID;

    return (
        <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Program level" required className="mb-0">
                    <Select
                        value={programLevelID ?? ""}
                        placeholder="Select"
                        onChange={(e) => onChange({ qualificationProgramLevelID: e.target.value || undefined })}
                    >
                        {(levels.data ?? []).map((l) => (
                            <option key={l.id} value={l.id}>
                                {Management.programLevelLabel(l)}
                            </option>
                        ))}
                    </Select>
                </Field>

                <Field label="Assessment cycle" required className="mb-0">
                    <Select
                        value={cycleID ?? ""}
                        placeholder="Select"
                        onChange={(e) => onChange({ assessmentCycleID: e.target.value || undefined })}
                    >
                        {(cycles.data ?? []).map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                                {c.academicYear?.name ? ` - ${c.academicYear.name}` : ""}
                            </option>
                        ))}
                    </Select>
                </Field>
            </div>

            {incomplete && <Banner tone="warning">This scope needs both a program level and a cycle. The server rejects it otherwise.</Banner>}
        </div>
    );
};

export default ScopeBuilder;
